import React, { useState, useEffect, useCallback } from 'react';
import { emailFlowApi } from '../services/api';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';

// ── helpers ──────────────────────────────────────────────────────────────────
const parseEmails = (str) => (str || '').split(';').map(e => e.trim()).filter(Boolean);
const joinEmails  = (arr) => (arr || []).join('; ');

const FREQ_COLORS = {
  'Daily':   { bg:'#EFF6FF', color:'#1D4ED8' },
  'Monthly': { bg:'#F0FDF4', color:'#15803D' },
  'Upon approval': { bg:'#FFF7ED', color:'#C2410C' },
};
const freqStyle = (f) => {
  for (const [k,v] of Object.entries(FREQ_COLORS)) {
    if ((f||'').toLowerCase().includes(k.toLowerCase())) return v;
  }
  return { bg:'#F8FAFC', color:'#475569' };
};

// ── Seed data from Excel (pre-loaded) ────────────────────────────────────────
const EXCEL_SEED = [
  { name:'Crucial Report', sendTime:'03:05', frequency:'Daily', internalEmails:'ananth_c@kaizeninfoserve.com;naveen_c@kaizeninfoserve.com', externalEmails:'pmunjal@micron.com;rajeshg@micron.com;ajunis@micron.com;sanjeeos@micron.com' },
  { name:'Zotac Report', sendTime:'04:01', frequency:'Daily', internalEmails:'naveen_c@kaizeninfoserve.com;dinesh_b@kaizeninfoserve.com', externalEmails:'technical@zotac.com' },
  { name:'Lexar Report', sendTime:'04:28', frequency:'Daily', internalEmails:'girish_c@kaizeninfoserve.com', externalEmails:'delian.huang@lexar.com;rajeev.anand@lexar.com;ajit.chanchal@lexar.com;pritesh.upadhyay@lexar.com;pallav.surai@lexar.com;brynn.zhou@lexar.com;zoey.liang@lexar.com;rajesh.yathraj@lexar.com;samanta.chandra@lexar.com;yolanda.zhong@lexar.com' },
  { name:'RMA Report - Lead', sendTime:'04:45', frequency:'Daily / When report edited', internalEmails:'naveen_c@kaizeninfoserve.com;rajesh_m@kaizeninfoserve.com;murali_krishnan@kaizeninfoserve.com;ananth_c@kaizeninfoserve.com;girish_c@kaizeninfoserve.com;gajendran_m@kaizeninfoserve.com', externalEmails:'' },
  { name:'Verbatim RMA Report', sendTime:'05:05', frequency:'Daily', internalEmails:'naveen_c@kaizeninfoserve.com', externalEmails:'' },
  { name:'Cooler-Master RMA Report', sendTime:'05:20', frequency:'Daily', internalEmails:'naveen_c@kaizeninfoserve.com', externalEmails:'Sanket_Naik@coolermaster.com.tw;gaurav_jain@coolermaster.co.in;gina_chen@coolermaster.com.tw;tony_wang@coolermaster.com.tw' },
  { name:'Galax Report', sendTime:'06:33', frequency:'Daily', internalEmails:'naveen_c@kaizeninfoserve.com;dinesh_b@kaizeninfoserve.com', externalEmails:'Thomas_chiu@galax.com;vikas@technologyandgadgets.com;dinesh.kumar@galax.com;marcoleung@galax.com;bankim@technologyandgadgets.com' },
  { name:'Corsair Report', sendTime:'06:54', frequency:'Daily', internalEmails:'murali_krishnan@kaizeninfoserve.com', externalEmails:'ellen.huang@corsair.com;Aaron.Shen@corsair.com' },
  { name:'Inno3D Report', sendTime:'08:02', frequency:'Daily', internalEmails:'naveen_c@kaizeninfoserve.com;dinesh_b@kaizeninfoserve.com', externalEmails:'stanley.chow@inno3d.com;support@inno3d.com;rmaindia@inno3d.com' },
  { name:'Verbatim Invoice', sendTime:'Upon approval', frequency:'Monthly', internalEmails:'naveen_c@kaizeninfoserve.com;murali_krishnan@kaizeninfoserve.com;mahesh_v@kaizeninfoserve.com', externalEmails:'RMattoo@Verbatim-europe.com;lalit.mathpal@verbatim.co.in;Sachin.Gosar@verbatim.co.in;accounting@verbatim-europe.com' },
  { name:'Creative Invoice', sendTime:'Upon approval', frequency:'Monthly', internalEmails:'ananth_c@kaizeninfoserve.com;murali_krishnan@kaizeninfoserve.com;mahesh_v@kaizeninfoserve.com', externalEmails:'wyseetho@ctl.creative.com;margaret_wong@ctl.creative.com' },
  { name:'Nextron Invoice', sendTime:'Upon approval', frequency:'Monthly', internalEmails:'girish_c@kaizeninfoserve.com;murali_krishnan@kaizeninfoserve.com;mahesh_v@kaizeninfoserve.com', externalEmails:'harshal@nextron.tech' },
  { name:'Crucial Invoice', sendTime:'Upon approval', frequency:'Monthly', internalEmails:'ananth_c@kaizeninfoserve.com;murali_krishnan@kaizeninfoserve.com;mahesh_v@kaizeninfoserve.com', externalEmails:'mel_ap1@micron.com;dkerner@micron.com' },
  { name:'Lexar Invoice', sendTime:'Upon approval', frequency:'Monthly', internalEmails:'girish_c@kaizeninfoserve.com;murali_krishnan@kaizeninfoserve.com;mahesh_v@kaizeninfoserve.com', externalEmails:'brynn.zhou@lexar.com' },
  { name:'Colorful DOA report', sendTime:'Upon excel upload to OneDrive', frequency:'On trigger', internalEmails:'murali_krishnan@kaizeninfoserve.com;gajendran_m@kaizeninfoserve.com', externalEmails:'rma@colorful.cn;arun@colorful.cn;sujaypais@colorful.cn;hemvardhan.shah@rptechindia.com;rajkumar.rajak@rptechindia.com;ashokg@rptechindia.com;Sampat.singh@rptechindia.com' },
  { name:'Colorful Billing', sendTime:'Upon excel upload to OneDrive', frequency:'Monthly', internalEmails:'murali_krishnan@kaizeninfoserve.com;gajendran_m@kaizeninfoserve.com;mahesh_v@kaizeninfoserve.com', externalEmails:'rma@colorful.cn;sujaypais@colorful.cn' },
  { name:'Colorful Invoice', sendTime:'Upon approval', frequency:'Monthly', internalEmails:'gajendran_m@kaizeninfoserve.com;murali_krishnan@kaizeninfoserve.com;mahesh_v@kaizeninfoserve.com', externalEmails:'rma@colorful.cn;sujaypais@colorful.cn' },
  { name:'Colorful RMA Report', sendTime:'Upon excel upload to OneDrive', frequency:'On trigger', internalEmails:'murali_krishnan@kaizeninfoserve.com;gajendran_m@kaizeninfoserve.com', externalEmails:'rma@colorful.cn;arun@colorful.cn;sujaypais@colorful.cn' },
  { name:'Biostar Report', sendTime:'Upon excel upload to OneDrive', frequency:'On trigger', internalEmails:'murali_krishnan@kaizeninfoserve.com;gajendran_m@kaizeninfoserve.com', externalEmails:'rick@biostar.com.tw;maggie@biostar.com.tw;shan@biostar.com.tw' },
  { name:'Micron Invoice', sendTime:'Upon approval', frequency:'Monthly', internalEmails:'naveen_c@kaizeninfoserve.com;murali_krishnan@kaizeninfoserve.com;mahesh_v@kaizeninfoserve.com', externalEmails:'moi_india_ap@micron.com;dkerner@micron.com' },
  { name:'Inno3D Invoice', sendTime:'Upon approval', frequency:'Monthly', internalEmails:'naveen_c@kaizeninfoserve.com;murali_krishnan@kaizeninfoserve.com;mahesh_v@kaizeninfoserve.com', externalEmails:'stanley.chow@inno3d.com;deepak.gupt@hotmail.com' },
  { name:'Galax Invoice', sendTime:'Upon approval', frequency:'Monthly', internalEmails:'naveen_c@kaizeninfoserve.com;murali_krishnan@kaizeninfoserve.com;mahesh_v@kaizeninfoserve.com', externalEmails:'vikas@technologyandgadgets.com;accounts@technologyandgadgets.com' },
  { name:'Cooler-Master Invoice', sendTime:'Upon approval', frequency:'Monthly', internalEmails:'naveen_c@kaizeninfoserve.com;murali_krishnan@kaizeninfoserve.com;mahesh_v@kaizeninfoserve.com', externalEmails:'gaurav_jain@coolermaster.co.in;sanket_naik@coolermaster.com.tw;libby_wu@coolermaster.com.cn;eva_chen@coolermaster.com.tw;grace_kuo@coolermaster.com.tw;gina_chen@coolermaster.com.tw' },
  { name:'Biostar Invoice', sendTime:'Upon approval', frequency:'Monthly', internalEmails:'gajendran_m@kaizeninfoserve.com;murali_krishnan@kaizeninfoserve.com;mahesh_v@kaizeninfoserve.com', externalEmails:'rick@biostar.com.tw;maggie@biostar.com.tw;shan@biostar.com.tw' },
  { name:'Biostar Billing', sendTime:'Upon excel upload to OneDrive', frequency:'Monthly', internalEmails:'gajendran_m@kaizeninfoserve.com;murali_krishnan@kaizeninfoserve.com;mahesh_v@kaizeninfoserve.com', externalEmails:'rick@biostar.com.tw;maggie@biostar.com.tw;shan@biostar.com.tw' },
  { name:'Biostar Weekly Report', sendTime:'Upon excel upload to OneDrive', frequency:'On trigger', internalEmails:'gajendran_m@kaizeninfoserve.com;murali_krishnan@kaizeninfoserve.com', externalEmails:'rick@biostar.com.tw;maggie@biostar.com.tw;shan@biostar.com.tw' },
  { name:'Email Reminder - Dollar-INR Rate', sendTime:'1st or 31st of month', frequency:'Monthly', internalEmails:'mahesh_v@kaizeninfoserve.com', externalEmails:'' },
  { name:'Zotac Invoice', sendTime:'Upon approval', frequency:'Monthly', internalEmails:'naveen_c@kaizeninfoserve.com;murali_krishnan@kaizeninfoserve.com;mahesh_v@kaizeninfoserve.com', externalEmails:'technical@zotac.com;deepak@zotac.com' },
  { name:'Array Invoice', sendTime:'Upon approval', frequency:'Monthly', internalEmails:'naveen_c@kaizeninfoserve.com;murali_krishnan@kaizeninfoserve.com;mahesh_v@kaizeninfoserve.com', externalEmails:'mmunis.sk@arraynetworks.net;msalman@arraynetworks.net;spaul@arraynetworks.net;nsuhail@arraynetworks.net;anuradha.joshi@panthco.co.in;shwetha.m@panthco.co.in;ahameed@arraynetworks.net' },
  { name:'Dispatch Report', sendTime:'19:00', frequency:'Daily', internalEmails:'mahesh_v@kaizeninfoserve.com;senthil_k@kaizeninfoserve.com;murali_krishnan@kaizeninfoserve.com', externalEmails:'' },
  { name:'BRYT Invoice', sendTime:'Upon approval', frequency:'Monthly', internalEmails:'ananth_c@kaizeninfoserve.com;murali_krishnan@kaizeninfoserve.com;mahesh_v@kaizeninfoserve.com', externalEmails:'vinay.ahuja@brytindia.com;niti.arora@brytindia.com' },
];

// ── Email chip component ──────────────────────────────────────────────────────
const EmailChips = ({ emails, type }) => {
  if (!emails || !emails.length) return <span style={{color:'var(--text-muted)',fontSize:12}}>—</span>;
  const color = type === 'internal' ? '#1D4ED8' : '#C2410C';
  const bg    = type === 'internal' ? '#EFF6FF' : '#FFF7ED';
  return (
    <div style={{display:'flex',flexWrap:'wrap',gap:4}}>
      {emails.slice(0,3).map(e=>(
        <span key={e} style={{fontSize:11,padding:'2px 7px',borderRadius:99,background:bg,color,fontWeight:500,maxWidth:180,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
          {e}
        </span>
      ))}
      {emails.length>3 && (
        <span style={{fontSize:11,padding:'2px 7px',borderRadius:99,background:'#F8FAFC',color:'var(--text-secondary)',fontWeight:500}}>
          +{emails.length-3} more
        </span>
      )}
    </div>
  );
};

// ── FlowModal (Add / Edit) ────────────────────────────────────────────────────
function FlowModal({ flow, onClose, onSave }) {
  const isNew = !flow;
  const [form, setForm] = useState({
    name:               flow?.name               || '',
    sendTime:           flow?.sendTime            || '',
    frequency:          flow?.frequency           || '',
    onedriveFolderPath: flow?.onedriveFolderPath  || '',
    onedriveFolderId:   flow?.onedriveFolderId    || '',
    reportFileName:     flow?.reportFileName      || '',
    internalEmails:     flow ? parseEmails(flow.internalEmails).join('\n') : '',
    externalEmails:     flow ? parseEmails(flow.externalEmails).join('\n') : '',
    notes:              flow?.notes               || '',
    isActive:           flow?.isActive            ?? true,
  });
  const [saving, setSaving] = useState(false);

  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        onedriveFolderId: form.onedriveFolderId.trim() || null,
        reportFileName:   form.reportFileName.trim()   || null,
        internalEmails: form.internalEmails.split(/[\n;,]+/).map(s=>s.trim()).filter(Boolean).join(';'),
        externalEmails: form.externalEmails.split(/[\n;,]+/).map(s=>s.trim()).filter(Boolean).join(';'),
      };
      if (isNew) { await emailFlowApi.create(payload);         toast.success('Flow created'); }
      else       { await emailFlowApi.update(flow.id, payload); toast.success('Flow updated'); }
      onSave(); onClose();
    } catch(err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{maxWidth:600,maxHeight:'90vh',overflowY:'auto'}} onClick={e=>e.stopPropagation()}>
        <div className="modal-title">{isNew ? 'Add email flow' : 'Edit email flow'}</div>
        <div className="modal-sub">{isNew ? 'Define a new report dispatch configuration' : `Editing: ${flow.name}`}</div>

        <form onSubmit={handleSubmit}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px 16px'}}>
            <div className="form-field" style={{gridColumn:'1/-1'}}>
              <label className="form-label">Report name *</label>
              <input className="form-input" value={form.name} onChange={e=>set('name',e.target.value)} required placeholder="e.g. Crucial Report"/>
            </div>
            <div className="form-field">
              <label className="form-label">Send time / trigger</label>
              <input className="form-input" value={form.sendTime} onChange={e=>set('sendTime',e.target.value)} placeholder="e.g. 03:05 or Upon approval"/>
            </div>
            <div className="form-field">
              <label className="form-label">Frequency</label>
              <select className="form-input" value={form.frequency} onChange={e=>set('frequency',e.target.value)}>
                <option value="">Select…</option>
                <option>Daily</option>
                <option>Daily / When report edited</option>
                <option>Monthly</option>
                <option>On trigger</option>
                <option>Upon approval</option>
                <option>Upon excel upload to OneDrive</option>
                <option>Other</option>
              </select>
            </div>
            <div className="form-field" style={{gridColumn:'1/-1'}}>
              <label className="form-label">OneDrive folder path <span style={{fontWeight:400,color:'var(--text-muted)',fontSize:11}}>display</span></label>
              <input className="form-input" value={form.onedriveFolderPath} onChange={e=>set('onedriveFolderPath',e.target.value)} placeholder="/Reports/Crucial"/>
            </div>
            <div className="form-field">
              <label className="form-label">OneDrive Folder ID</label>
              <input className="form-input" value={form.onedriveFolderId} onChange={e=>set('onedriveFolderId',e.target.value)}
                placeholder="01ABCDEF123456789" style={{fontFamily:'monospace',fontSize:12}}/>
              <div style={{fontSize:11,color:'var(--text-muted)',marginTop:3}}>
                From Graph Explorer: <code style={{fontSize:10,background:'#F1F5F9',padding:'1px 4px',borderRadius:3}}>GET /me/drive/root:/FolderName</code> → copy "id"
              </div>
            </div>
            <div className="form-field">
              <label className="form-label">Report file name <span style={{fontWeight:400,color:'var(--text-muted)',fontSize:11}}>optional</span></label>
              <input className="form-input" value={form.reportFileName} onChange={e=>set('reportFileName',e.target.value)}
                placeholder="Crucial_Report.xlsx" style={{fontFamily:'monospace',fontSize:12}}/>
              <div style={{fontSize:11,color:'var(--text-muted)',marginTop:3}}>
                If blank, the most recently modified file in the folder is used
              </div>
            </div>
            <div className="form-field">
              <label className="form-label">Internal emails (Kaizen)</label>
              <textarea className="form-input" rows={4} value={form.internalEmails}
                onChange={e=>set('internalEmails',e.target.value)}
                placeholder="one@kaizeninfoserve.com&#10;two@kaizeninfoserve.com"
                style={{resize:'vertical',fontFamily:'monospace',fontSize:12}}/>
              <div style={{fontSize:11,color:'var(--text-muted)',marginTop:3}}>One per line or semicolon-separated</div>
            </div>
            <div className="form-field">
              <label className="form-label">External emails (Clients)</label>
              <textarea className="form-input" rows={4} value={form.externalEmails}
                onChange={e=>set('externalEmails',e.target.value)}
                placeholder="client@example.com&#10;partner@company.com"
                style={{resize:'vertical',fontFamily:'monospace',fontSize:12}}/>
              <div style={{fontSize:11,color:'var(--text-muted)',marginTop:3}}>One per line or semicolon-separated</div>
            </div>
            <div className="form-field" style={{gridColumn:'1/-1'}}>
              <label className="form-label">Notes</label>
              <input className="form-input" value={form.notes} onChange={e=>set('notes',e.target.value)} placeholder="Any additional notes…"/>
            </div>
            {!isNew && (
              <div className="form-field" style={{display:'flex',alignItems:'center',gap:10}}>
                <input type="checkbox" id="ef-active" checked={form.isActive} onChange={e=>set('isActive',e.target.checked)}/>
                <label htmlFor="ef-active" style={{fontSize:13,color:'var(--text-secondary)'}}>Flow active</label>
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : isNew ? 'Create flow' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Detail drawer ─────────────────────────────────────────────────────────────
function FlowDetail({ flow, onClose, onEdit }) {
  const intEmails = parseEmails(flow.internalEmails);
  const extEmails = parseEmails(flow.externalEmails);
  const fs = freqStyle(flow.frequency);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{maxWidth:560,maxHeight:'90vh',overflowY:'auto'}} onClick={e=>e.stopPropagation()}>
        <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:16}}>
          <div>
            <div className="modal-title">{flow.name}</div>
            <div style={{display:'flex',gap:8,marginTop:6,flexWrap:'wrap'}}>
              <span style={{fontSize:11,padding:'3px 9px',borderRadius:99,background:fs.bg,color:fs.color,fontWeight:700}}>
                {flow.frequency||'—'}
              </span>
              <span style={{fontSize:11,padding:'3px 9px',borderRadius:99,background:'#F8FAFC',color:'var(--text-secondary)',fontWeight:600}}>
                ⏰ {flow.sendTime||'—'}
              </span>
              <span style={{fontSize:11,padding:'3px 9px',borderRadius:99,
                background:flow.isActive?'var(--success-bg)':'var(--danger-bg)',
                color:flow.isActive?'var(--success)':'var(--danger)',fontWeight:700}}>
                {flow.isActive?'Active':'Inactive'}
              </span>
            </div>
          </div>
          <button className="btn-icon" onClick={onClose} style={{flexShrink:0}}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {flow.onedriveFolderPath && (
          <div style={{background:'#F8FAFC',border:'1px solid var(--border)',borderRadius:8,padding:'10px 14px',marginBottom:16,display:'flex',gap:8,alignItems:'center'}}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F97316" strokeWidth="2"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>
            <span style={{fontSize:13,color:'var(--text-secondary)'}}>OneDrive: <strong style={{color:'var(--text-primary)',fontFamily:'monospace',fontSize:12}}>{flow.onedriveFolderPath}</strong></span>
          </div>
        )}

        <div style={{marginBottom:16}}>
          <div style={{fontSize:12,fontWeight:700,color:'var(--text-secondary)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:8}}>
            Internal recipients ({intEmails.length}) — Kaizen team
          </div>
          {intEmails.length ? intEmails.map(e=>(
            <div key={e} style={{display:'flex',alignItems:'center',gap:8,padding:'6px 0',borderBottom:'1px solid var(--border)'}}>
              <div style={{width:28,height:28,borderRadius:'50%',background:'#EFF6FF',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700,color:'#1D4ED8',flexShrink:0}}>
                {e[0].toUpperCase()}
              </div>
              <span style={{fontSize:13,fontFamily:'monospace'}}>{e}</span>
            </div>
          )) : <div style={{fontSize:13,color:'var(--text-muted)'}}>No internal recipients</div>}
        </div>

        <div style={{marginBottom:20}}>
          <div style={{fontSize:12,fontWeight:700,color:'var(--text-secondary)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:8}}>
            External recipients ({extEmails.length}) — Clients
          </div>
          {extEmails.length ? extEmails.map(e=>(
            <div key={e} style={{display:'flex',alignItems:'center',gap:8,padding:'6px 0',borderBottom:'1px solid var(--border)'}}>
              <div style={{width:28,height:28,borderRadius:'50%',background:'#FFF7ED',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700,color:'#C2410C',flexShrink:0}}>
                {e[0].toUpperCase()}
              </div>
              <span style={{fontSize:13,fontFamily:'monospace'}}>{e}</span>
            </div>
          )) : <div style={{fontSize:13,color:'var(--text-muted)'}}>No external recipients</div>}
        </div>

        {flow.notes && (
          <div style={{background:'#FFFBEB',border:'1px solid #FDE68A',borderRadius:8,padding:'10px 14px',marginBottom:16,fontSize:13,color:'#92400E'}}>
            📝 {flow.notes}
          </div>
        )}

        <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          <button className="btn btn-primary" onClick={()=>{ onClose(); onEdit(flow); }}>Edit flow</button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function EmailFlowPage() {
  const [flows, setFlows]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [importing, setImporting] = useState(false);
  const [search, setSearch]       = useState('');
  const [freqFilter, setFreqFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editFlow, setEditFlow]   = useState(null);
  const [detailFlow, setDetailFlow] = useState(null);
  const [viewMode, setViewMode]   = useState('table'); // 'table' | 'card'

  const loadFlows = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (freqFilter) params.frequency = freqFilter;
      const res = await emailFlowApi.list(params);
      setFlows(res.data.flows);
    } catch { toast.error('Failed to load email flows'); }
    finally { setLoading(false); }
  }, [search, freqFilter]);

  useEffect(() => { loadFlows(); }, [loadFlows]);

  const handleImport = async () => {
    setImporting(true);
    try {
      const res = await emailFlowApi.importBulk(EXCEL_SEED);
      toast.success(`Import complete: ${res.data.created} created, ${res.data.updated} updated`);
      loadFlows();
    } catch(err) {
      toast.error(err.response?.data?.error || 'Import failed');
    } finally { setImporting(false); }
  };

  const handleDelete = async (flow) => {
    if (!window.confirm(`Delete "${flow.name}"? This cannot be undone.`)) return;
    try {
      await emailFlowApi.delete(flow.id);
      toast.success('Flow deleted');
      loadFlows();
    } catch(err) { toast.error(err.response?.data?.error || 'Delete failed'); }
  };

  const freqs = [...new Set(flows.map(f=>f.frequency).filter(Boolean))].sort();
  const stats = {
    total:    flows.length,
    daily:    flows.filter(f=>(f.frequency||'').toLowerCase().includes('daily')).length,
    monthly:  flows.filter(f=>(f.frequency||'').toLowerCase().includes('monthly')).length,
    trigger:  flows.filter(f=>(f.frequency||'').toLowerCase().includes('trigger')||
                               (f.frequency||'').toLowerCase().includes('approval')||
                               (f.frequency||'').toLowerCase().includes('upload')).length,
    inactive: flows.filter(f=>!f.isActive).length,
  };

  return (
    <>
      {showModal && (
        <FlowModal flow={editFlow}
          onClose={()=>{ setShowModal(false); setEditFlow(null); }}
          onSave={loadFlows}/>
      )}
      {detailFlow && (
        <FlowDetail flow={detailFlow}
          onClose={()=>setDetailFlow(null)}
          onEdit={(f)=>{ setEditFlow(f); setShowModal(true); }}/>
      )}

      {/* Page header */}
      <div className="page-header">
        <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',paddingBottom:16,flexWrap:'wrap',gap:12}}>
          <div>
            <div className="page-title">Email Dispatch Flows</div>
            <div className="page-subtitle">{flows.length} report dispatch configurations · Admin only</div>
          </div>
          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
            {flows.length === 0 && (
              <button className="btn btn-secondary" onClick={handleImport} disabled={importing}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                {importing ? 'Importing…' : 'Import from Excel'}
              </button>
            )}
            {flows.length > 0 && (
              <button className="btn btn-secondary" onClick={handleImport} disabled={importing} title="Re-import / update from Excel data">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
                {importing ? 'Updating…' : 'Re-sync Excel'}
              </button>
            )}
            <button className="btn btn-primary" onClick={()=>{ setEditFlow(null); setShowModal(true); }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Add flow
            </button>
          </div>
        </div>
      </div>

      <div className="page-body">
        {/* Stats row */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))',gap:12,marginBottom:20}}>
          {[
            { label:'Total flows',  val:stats.total,    color:'#F97316' },
            { label:'Daily',        val:stats.daily,    color:'#1D4ED8' },
            { label:'Monthly',      val:stats.monthly,  color:'#15803D' },
            { label:'On trigger',   val:stats.trigger,  color:'#C2410C' },
            { label:'Inactive',     val:stats.inactive, color:'#6B7280' },
          ].map(s=>(
            <div key={s.label} className="stat-card" style={{padding:'14px 16px'}}>
              <div className="stat-label">{s.label}</div>
              <div className="stat-val" style={{fontSize:22,color:s.color}}>{s.val}</div>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap',alignItems:'center'}}>
          <div className="search-box" style={{flex:1,minWidth:200}}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input placeholder="Search report name or email…" value={search} onChange={e=>setSearch(e.target.value)}/>
          </div>
          <select className="form-input" style={{width:'auto'}} value={freqFilter} onChange={e=>setFreqFilter(e.target.value)}>
            <option value="">All frequencies</option>
            {freqs.map(f=><option key={f} value={f}>{f}</option>)}
          </select>
          <div style={{display:'flex',border:'1px solid var(--border)',borderRadius:6,overflow:'hidden'}}>
            <button onClick={()=>setViewMode('table')} style={{padding:'7px 12px',border:'none',cursor:'pointer',fontSize:13,background:viewMode==='table'?'var(--accent)':'white',color:viewMode==='table'?'white':'var(--text-secondary)',transition:'all 0.15s'}}>
              ☰ Table
            </button>
            <button onClick={()=>setViewMode('card')} style={{padding:'7px 12px',border:'none',cursor:'pointer',fontSize:13,background:viewMode==='card'?'var(--accent)':'white',color:viewMode==='card'?'white':'var(--text-secondary)',transition:'all 0.15s'}}>
              ⊞ Cards
            </button>
          </div>
        </div>

        {/* Empty state with import CTA */}
        {!loading && flows.length === 0 && (
          <div className="empty-state" style={{padding:'48px 24px'}}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
            <h3>No email flows configured</h3>
            <p>Import from the Excel file to get started, or add flows manually.</p>
            <button className="btn btn-primary" style={{marginTop:16}} onClick={handleImport} disabled={importing}>
              {importing ? 'Importing 31 flows…' : '↓ Import all 31 flows from Excel'}
            </button>
          </div>
        )}

        {loading && (
          <div style={{display:'flex',justifyContent:'center',padding:'48px 0'}}>
            <div className="spinner"/>
          </div>
        )}

        {/* TABLE VIEW */}
        {!loading && flows.length > 0 && viewMode === 'table' && (
          <div className="card">
            <div style={{overflowX:'auto'}}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Report name</th>
                    <th>Frequency</th>
                    <th>Send time</th>
                    <th>Internal (Kaizen)</th>
                    <th>External (Client)</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {flows.map(f => {
                    const fs = freqStyle(f.frequency);
                    const intE = parseEmails(f.internalEmails);
                    const extE = parseEmails(f.externalEmails);
                    return (
                      <tr key={f.id} style={{cursor:'pointer'}} onClick={()=>setDetailFlow(f)}>
                        <td>
                          <div style={{fontWeight:600,fontSize:13}}>{f.name}</div>
                          {f.onedriveFolderPath && (
                            <div style={{fontSize:11,color:'var(--text-muted)',fontFamily:'monospace',marginTop:2}}>
                              📁 {f.onedriveFolderPath}
                            </div>
                          )}
                        </td>
                        <td onClick={e=>e.stopPropagation()}>
                          <span style={{fontSize:11,padding:'3px 9px',borderRadius:99,background:fs.bg,color:fs.color,fontWeight:700,whiteSpace:'nowrap'}}>
                            {f.frequency||'—'}
                          </span>
                        </td>
                        <td style={{fontSize:12,color:'var(--text-secondary)',whiteSpace:'nowrap'}}>{f.sendTime||'—'}</td>
                        <td onClick={e=>e.stopPropagation()}><EmailChips emails={intE} type="internal"/></td>
                        <td onClick={e=>e.stopPropagation()}><EmailChips emails={extE} type="external"/></td>
                        <td onClick={e=>e.stopPropagation()}>
                          <span style={{fontSize:11,padding:'3px 8px',borderRadius:99,fontWeight:700,
                            background:f.isActive?'var(--success-bg)':'var(--danger-bg)',
                            color:f.isActive?'var(--success)':'var(--danger)'}}>
                            {f.isActive?'Active':'Off'}
                          </span>
                        </td>
                        <td onClick={e=>e.stopPropagation()}>
                          <div style={{display:'flex',gap:5}}>
                            <button className="btn btn-secondary" style={{padding:'4px 10px',fontSize:12}}
                              onClick={()=>setDetailFlow(f)}>View</button>
                            <button className="btn btn-secondary" style={{padding:'4px 10px',fontSize:12}}
                              onClick={()=>{ setEditFlow(f); setShowModal(true); }}>Edit</button>
                            <button className="btn btn-danger" style={{padding:'4px 8px',fontSize:12}}
                              onClick={()=>handleDelete(f)}>✕</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* CARD VIEW */}
        {!loading && flows.length > 0 && viewMode === 'card' && (
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',gap:14}}>
            {flows.map(f => {
              const fs = freqStyle(f.frequency);
              const intE = parseEmails(f.internalEmails);
              const extE = parseEmails(f.externalEmails);
              return (
                <div key={f.id} className="card" style={{padding:16,cursor:'pointer',transition:'border-color 0.2s,box-shadow 0.2s'}}
                  onClick={()=>setDetailFlow(f)}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor='var(--accent)';e.currentTarget.style.boxShadow='0 0 0 3px rgba(249,115,22,0.1)';}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.boxShadow='none';}}>
                  <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:10}}>
                    <div style={{fontWeight:700,fontSize:14,flex:1,lineHeight:1.3}}>{f.name}</div>
                    <span style={{fontSize:10,padding:'2px 7px',borderRadius:99,background:f.isActive?'var(--success-bg)':'var(--danger-bg)',color:f.isActive?'var(--success)':'var(--danger)',fontWeight:700,flexShrink:0,marginLeft:8}}>
                      {f.isActive?'ON':'OFF'}
                    </span>
                  </div>
                  <div style={{display:'flex',gap:6,marginBottom:12,flexWrap:'wrap'}}>
                    <span style={{fontSize:11,padding:'2px 8px',borderRadius:99,background:fs.bg,color:fs.color,fontWeight:600}}>{f.frequency||'—'}</span>
                    <span style={{fontSize:11,padding:'2px 8px',borderRadius:99,background:'#F8FAFC',color:'var(--text-secondary)',fontWeight:500}}>⏰ {f.sendTime||'—'}</span>
                  </div>
                  <div style={{fontSize:11,color:'var(--text-secondary)',marginBottom:4,fontWeight:600}}>
                    INTERNAL ({intE.length})
                  </div>
                  <EmailChips emails={intE} type="internal"/>
                  <div style={{fontSize:11,color:'var(--text-secondary)',margin:'8px 0 4px',fontWeight:600}}>
                    EXTERNAL ({extE.length})
                  </div>
                  <EmailChips emails={extE} type="external"/>
                  {f.onedriveFolderPath && (
                    <div style={{marginTop:10,fontSize:11,color:'var(--text-muted)',fontFamily:'monospace',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                      📁 {f.onedriveFolderPath}
                    </div>
                  )}
                  <div style={{display:'flex',gap:6,marginTop:12}} onClick={e=>e.stopPropagation()}>
                    <button className="btn btn-secondary" style={{flex:1,padding:'5px 0',fontSize:12,justifyContent:'center'}}
                      onClick={()=>{ setEditFlow(f); setShowModal(true); }}>Edit</button>
                    <button className="btn btn-danger" style={{padding:'5px 10px',fontSize:12}}
                      onClick={()=>handleDelete(f)}>Delete</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
